import { useState, useMemo, useRef, useEffect } from "react";
import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";
import styles from "./ComparisonGraph.module.css"

Chart.register(zoomPlugin);



function BarComparison({ current, previous, historical = false, res, xInterval }) {

    const EMPTY_CHART = {
        labels: [],
        datasets: [],
    };

    const [currentChartData, setCurrentChartData] = useState(EMPTY_CHART);
    const [previousChartData, setPreviousChartData] = useState(EMPTY_CHART);
    const [currentChartOptions, setCurrentChartOptions] = useState({});
    const [previousChartOptions, setPreviousChartOptions] = useState({});

    const [currentMinChartWidth, setCurrentMinChartWidth] = useState();
    const [previousMinChartWidth, setPreviousMinChartWidth] = useState();
    const [currentXRange, setCurrentXRange] = useState({});
    const [previousXRange, setPreviousXRange] = useState({});
    const graphRef = useRef(null);

    function getRandomColor() {
        const r = Math.floor(Math.random() * 200);
        const g = Math.floor(Math.random() * 200);
        const b = Math.floor(Math.random() * 200);
        return `rgba(${r},${g},${b},0.8)`;
    }




    function convertToBarChartData(source) {
        if (!source || !source.datasets || !source.headers || !source.labels) return null;

        const labels = source.headers;

        // ---- Average calculation ----
        const avgValues = source.datasets.map(ds => {
            const nums = ds.data
                .map(v => Number(v))
                .filter(v => !isNaN(v));

            if (!nums.length) return 0;

            const sum = nums.reduce((a, b) => a + b, 0);
            return +(sum / nums.length).toFixed(2);
        });

        // ---- Start & End date ----
        
        const startDate = new Date(source.labels[0]).toLocaleString(); // or toLocaleDateString() for only date
        const endDate = new Date(source.labels[source.labels.length - 1]).toLocaleString();

        return {
            labels,
            datasets: [
                {
                    label: `${startDate}  →  ${endDate}`,
                    data: avgValues,
                    backgroundColor: labels.map(() => getRandomColor()),
                },
            ],
        };
    }

    useEffect(() => {

        setCurrentXRange({
            min: current?.labels[0],
            max: current?.labels[current?.labels?.length - 1],
        });

        const barChartData = convertToBarChartData(current);
        setCurrentChartData(barChartData);

    }, [current])

    useEffect(() => {
        setPreviousXRange({
            min: previous?.labels[0],
            max: previous?.labels[previous?.labels?.length - 1],
        });

        const barChartData = convertToBarChartData(previous);

        setPreviousChartData(barChartData);

    }, [previous])

    /* ---------------- OPTIONS ---------------- */
    useEffect(() => {
        setCurrentChartOptions({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: {
                    ticks: {
                        fontSize: 10
                    }
                },

            },
            plugins: {
                decimation: {
                    enabled: true,
                    algorithm: "lttb",
                    samples: res ?? 1,
                    threshold: 200,
                },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: "y",
                        modifierKey: "ctrl",
                    },
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: "y",
                    },
                },
                legend: {
                    align: "left",
                    labels: {
                        boxWidth: 5,
                        font: { size: 10 },
                    },
                },
            },
        });
    }, [xInterval, res, currentMinChartWidth, currentXRange]);

    /* ---------------- OPTIONS ---------------- */
    useEffect(() => {
        setPreviousChartOptions({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: {
                    ticks: {
                        fontSize: 6
                    }
                },

            },
            plugins: {
                decimation: {
                    enabled: true,
                    algorithm: "lttb",
                    samples: res ?? 1,
                    threshold: 200,
                },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: "y",
                        modifierKey: "ctrl",
                    },
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: "y",
                    },
                },
                legend: {
                    align: "left",
                    labels: {
                        boxWidth: 5,
                        font: { size: 10 },
                    },
                },
            },
        });
    }, [xInterval, res, previousMinChartWidth, previousXRange]);



    return (

        <>
            <div className={styles.graphWrapper}>
                <div className={styles.graphContainer}>
                    <h4 className={styles.graphTitle}>Current Range</h4>
                    <div className={styles.chartBox}>
                        <div
                            style={{
                                width: "100%",
                                height: "100%",
                                overflowX: xInterval ? "auto" : "hidden",
                                overflowY: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    minWidth: xInterval ? currentMinChartWidth : "100%",
                                    height: "350px",
                                }}
                            >
                                {currentChartData?.labels?.length > 0 && (
                                    <Bar data={currentChartData} options={currentChartOptions}/>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.graphContainer}>
                    <h4 className={styles.graphTitle}>Previous Range</h4>
                    <div className={styles.chartBox}>
                        <div
                            style={{
                                width: "100%",
                                height: "100%",
                                overflowX: xInterval ? "auto" : "hidden",
                                overflowY: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    minWidth: xInterval ? previousMinChartWidth : "100%",
                                    height: "350px",
                                }}
                            >
                                {previousChartData?.labels?.length > 0 && (
                                    <Bar data={previousChartData} options={previousChartOptions}/>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>

    );
}

export default BarComparison;
